/* eslint-disable clubmed/no-fake-component */
import React from 'react';
import ReactSlider from 'react-slider';
import moment from 'moment';
import { t } from 'Src/i18n';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ICONS } from 'Components/utils/icon/icons';
import {
  getRangeTimeGraduations,
  MAX_MINUTES_IN_CURRENT_HOUR,
} from './ArrivalDepartureRangeFilter.utils';

/**
 * LOCAL COMPONENT RENDERERS
 */
// render the slider track
const renderTrack = (props, state) => {
  let bgClass;

  switch (state.index) {
    case 0:
      bgClass = 'bg-lightGrey';
      break;

    case 1:
      bgClass = 'bg-saffron';
      break;

    default:
      bgClass = 'bg-lightGrey';
      break;
  }

  return <div {...props} className={'example-track h-4 ' + bgClass}></div>;
};

/**
 * END LOCAL COMPONENT RENDERERS
 */
type ArrivalDepartureRangeFilterPropsType = {
  startTime: moment$Moment;
  endTime: moment$Moment;
  setRange: (arg0: { start: moment$Moment; end: moment$Moment }) => void;
  selectedRange: {
    start: moment$Moment;
    end: moment$Moment;
  };
  showSelectedTimeUnderSlider?: boolean;
};
export const ArrivalDepartureRangeFilter = ({
  startTime,
  endTime,
  setRange,
  selectedRange,
  showSelectedTimeUnderSlider = true,
}: ArrivalDepartureRangeFilterPropsType) => {
  const { graduations, stepsNumber } = getRangeTimeGraduations({
    startTime,
    endTime,
  });
  const { trackClickEvent } = useTrackEvent();

  // thumb component : min max visual component
  const renderThumb = (props, state) => {
    const graduationIndex = state.valueNow === 0 ? 0 : state.valueNow - 1;
    const selectedTime = graduations[graduationIndex];
    return (
      <div data-testid="ArrivalDepartureRangeFilter-thumb" {...props}>
        <div
          data-testid="ArrivalDepartureRangeFilter-thumbIncon"
          className="flex h-24 w-24 items-center justify-center rounded-2 border-1 bg-white"
        >
          <Icon name={ICONS.PAUSE} width=".5rem" />
        </div>
        {!showSelectedTimeUnderSlider && (
          <div
            data-testid="ArrivalDepartureRangeFilter-timeUnderThumb"
            className="absolute w-full text-center"
          >
            <span className="text-10 font-bold">{selectedTime}</span>
          </div>
        )}
      </div>
    );
  };

  // on change handler
  const handleChangeSliderValues = (values) => {
    const [start, end] = values;
    const [startedAt, endedAt] = [graduations[start - 1], graduations[end - 1]];
    // transform to moment date
    const startedAtMoment = moment(startedAt, 'HH[h]mm');
    const endedAtMoment = moment(endedAt, 'HH[h]mm');
    // set the selected Range to the parent component
    setRange &&
      setRange({
        start: startedAtMoment,
        end: endedAtMoment,
      });
  };

  // on change handler
  const getValueFromRange = (range: { start: moment$Moment; end: moment$Moment }) => {
    const start = range.start.format('HH[h]');
    const timeFormat = range.end.minutes() === MAX_MINUTES_IN_CURRENT_HOUR ? 'HH[h]mm' : 'HH[h]';
    const end = range.end.format(timeFormat);
    return [graduations.indexOf(start) + 1, graduations.indexOf(end) + 1];
  };

  return (
    <div>
      <div data-testid="ArrivalDepartureRangeFilter-departureDate">
        <span className="text-14 font-bold">{t('booking.transport.departureNextDay')}</span>
      </div>
      <ReactSlider
        className="horizontal-slider relative flex h-16 w-full items-center py-30"
        trackClassName="bg-gray-200"
        min={1}
        max={stepsNumber}
        defaultValue={[0, stepsNumber]}
        renderThumb={renderThumb}
        renderTrack={renderTrack}
        onChange={handleChangeSliderValues}
        onAfterChange={() =>
          trackClickEvent(TRACK_EVENT_NAMES.CLICK_TRANSPORT_LAYER, {
            detail_click: 'filter_schedule',
          })
        }
        value={getValueFromRange(selectedRange)}
        minDistance={1}
      />
      {showSelectedTimeUnderSlider && (
        <div
          className="flex justify-between"
          data-testid="ArrivalDepartureRangeFilter-timeUnderSlider"
        >
          <div data-testid="ArrivalDepartureRangeFilter-minRange" className="">
            <span className="text-14 font-bold">{selectedRange.start?.format('HH[h]mm')}</span>
          </div>
          <div data-testid="ArrivalDepartureRangeFilter-maxRange" className="">
            <span className="text-14 font-bold">{selectedRange.end?.format('HH[h]mm')}</span>
          </div>
        </div>
      )}
    </div>
  );
};
